import { useState, useEffect } from 'react';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import mockData from '../mocks/mapData.json';

/** モック用: スポットIDをシードにしたダミーユーザーリストを生成 */
const generateMockUsers = (spotId, stampCount) => {
  const count = Math.min(stampCount, 5);
  return Array.from({ length: count }, (_, i) => {
    const num = i + 1;
    return {
      userId: `mock_user_${spotId}_${num}`,
      displayName: `テストユーザー${num}`,
      acquiredAt: { toDate: () => new Date(2025, 3, num, 10 + i, 0, 0) },
    };
  });
};

/**
 * スポット詳細データ取得フック
 *
 * - useMock=true: src/mocks/mapData.json を返す（開発用）
 * - useMock=false: Firestore /mapSpots/{spotId} + /mapSpots/{spotId}/users を取得
 *
 * @param {string | null} spotId - スポットID（null の場合はフェッチしない）
 * @param {boolean} useMock - モックデータを使用するか
 * @returns {{
 *   spot: Object | null,
 *   stampUsers: Array,
 *   loading: boolean,
 *   error: Error | null
 * }}
 */
const useSpotDetail = (spotId, useMock = false) => {
  const [spot, setSpot] = useState(null);
  const [stampUsers, setStampUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!spotId) {
      setSpot(null);
      setStampUsers([]);
      return;
    }

    // ── モックモード ──────────────────────────────────────────
    if (useMock) {
      setLoading(true);
      const found = mockData.spots.find((s) => s.id === spotId) ?? null;
      // spotMeta からサムネイルを注入（SpotDetailSheet が参照する thumbnailUrls を補完）
      const meta = (mockData.spotMeta ?? {})[spotId] ?? { thumbnailUrls: [], address: '' };
      const spotWithMeta = found ? { ...found, ...meta } : null;
      setSpot(spotWithMeta);
      setStampUsers(found ? generateMockUsers(spotId, found.stampCount) : []);
      setLoading(false);
      return;
    }

    // ── 本番モード（Firestore） ───────────────────────────────
    let cancelled = false;
    const startTime = Date.now();
    const TIMEOUT_MS = 10000; // 10秒タイムアウト
    
    setLoading(true);
    setError(null);

    console.debug(`[useSpotDetail] 📍 スポット詳細取得開始: spotId=${spotId}, time=${new Date().toISOString()}`);

    const spotRef = doc(db, 'mapSpots', spotId);
    const usersRef = collection(db, 'mapSpots', spotId, 'users');
    const usersQuery = query(usersRef, orderBy('acquiredAt', 'asc'));

    // タイムアウト処理
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        const elapsed = Date.now() - startTime;
        const timeoutError = new Error(`Firestore取得がタイムアウトしました (${elapsed}ms)`);
        console.error(`[useSpotDetail] ⏱️ タイムアウト: spotId=${spotId}, elapsed=${elapsed}ms`);
        setError(timeoutError);
        setLoading(false);
        cancelled = true;
      }
    }, TIMEOUT_MS);

    Promise.all([getDoc(spotRef), getDocs(usersQuery)])
      .then(([spotSnap, usersSnap]) => {
        const elapsed = Date.now() - startTime;
        console.debug(`[useSpotDetail] ✅ データ取得完了: spotId=${spotId}, elapsed=${elapsed}ms, spot=${spotSnap.exists()}, users=${usersSnap.docs.length}`);
        
        if (cancelled) {
          console.debug(`[useSpotDetail] 🚫 キャンセル済みのため処理をスキップ: spotId=${spotId}`);
          return;
        }
        
        clearTimeout(timeoutId);
        setSpot(
          spotSnap.exists() ? { id: spotSnap.id, ...spotSnap.data() } : null
        );
        setStampUsers(
          usersSnap.docs.map((d) => ({ userId: d.id, ...d.data() }))
        );
        setLoading(false);
      })
      .catch((err) => {
        const elapsed = Date.now() - startTime;
        console.error(`[useSpotDetail] ❌ Firestore エラー: spotId=${spotId}, elapsed=${elapsed}ms, error=${err.message}, code=${err.code}`);
        
        if (!cancelled) {
          clearTimeout(timeoutId);
          setError(err);
          setLoading(false);
        }
      });

    return () => {
      console.debug(`[useSpotDetail] 🧹 クリーンアップ実行: spotId=${spotId}`);
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [spotId, useMock]);

  return { spot, stampUsers, loading, error };
};

export default useSpotDetail;
